import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { portalApi } from "@/lib/portalApi";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Settings } from "lucide-react";

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Phoenix", label: "Arizona (MST, no DST)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HST)" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const ampm = i < 12 ? "AM" : "PM";
  const h = i === 0 ? 12 : i > 12 ? i - 12 : i;
  return { value: String(i), label: `${h}:00 ${ampm}` };
});

export default function PortalSettings() {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);

  const { data: me } = useQuery({
    queryKey: ["portal", "me"],
    queryFn: () => portalApi.me(),
  });

  const { data: prefs, isLoading } = useQuery({
    queryKey: ["portal", "preferences"],
    queryFn: () => portalApi.preferences(),
  });

  const [timezone, setTimezone] = useState<string | undefined>(undefined);
  const [startHour, setStartHour] = useState<string | undefined>(undefined);
  const [endHour, setEndHour] = useState<string | undefined>(undefined);

  const effectiveTz = timezone ?? prefs?.timezone ?? "America/New_York";
  const effectiveStart = startHour ?? String(prefs?.notification_start_hour ?? 8);
  const effectiveEnd = endHour ?? String(prefs?.notification_end_hour ?? 20);

  const saveMutation = useMutation({
    mutationFn: () =>
      portalApi.updatePreferences({
        timezone: effectiveTz,
        notification_start_hour: Number(effectiveStart),
        notification_end_hour: Number(effectiveEnd),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portal", "preferences"] });
      // Clear local overrides so the refetched server values are the source of truth
      setTimezone(undefined);
      setStartHour(undefined);
      setEndHour(undefined);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: () => {
      // Drop unsaved changes so the UI doesn't pretend the save succeeded
      setTimezone(undefined);
      setStartHour(undefined);
      setEndHour(undefined);
    },
  });

  return (
    <PortalLayout accountName={me?.account_name}>
      <div className="space-y-6 max-w-lg">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">Manage your portal preferences.</p>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading…</span>
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Notification Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-1.5">
                <Label>Timezone</Label>
                <Select value={effectiveTz} onValueChange={setTimezone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Used to display dates and times correctly.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Notifications start</Label>
                  <Select value={effectiveStart} onValueChange={setStartHour}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map(h => (
                        <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Notifications end</Label>
                  <Select value={effectiveEnd} onValueChange={setEndHour}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map(h => (
                        <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Reminder emails from your accountant will only be sent during these hours.
              </p>

              <div className="flex items-center gap-3 pt-1">
                <Button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                  Save preferences
                </Button>
                {saved && <span className="text-sm text-green-600 font-medium">Saved ✓</span>}
                {saveMutation.isError && (
                  <span className="text-sm text-destructive">Failed to save.</span>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PortalLayout>
  );
}
